import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import type { ISPD } from '../../sprites/sprites'
import { sprites } from '../../sprites/sprites-sidecar'
import { chessGameMode_game_layout } from './chessGameMode_game_layout-sidecar'
import { EChessTeam, IChessPiece } from './chessPieces'
import { chessPiecesUtil } from './chessPiecesUtil-sidecar'
import { chessTilemap } from './chessTilemap-sidecar'
import type { IChessTilemapPart } from './chessTilemapPart'

const namespace = 'chess-pieces'

const isRotated = chessGameMode_game_layout.isRotated

export enum EChessPieceType {
  Default = -1,
  Null = 0,
  Pawn = 1,
  Rook = 2,
  Bishop = 3,
  Knight = 4,
  Queen = 5,
  King = 6,
}

export interface IChessPieceData {
  type: EChessPieceType
  name: string
  frames: ISPD[]
  canMove: (obj: IChessPiece, tp: IChessTilemapPart) => boolean
  pointCost: number
  oy: number
  ox: number
}

const _default: IChessPieceData = {
  type: EChessPieceType.Default,
  name: 'Default',
  frames: [sprites.chessCircle[0], sprites.chessSquare[0]],
  canMove: () => {
    return true
  },
  pointCost: 0,
  oy: 0,
  ox: 0,
}

let dataList: IChessPieceData[] = []
let dataMap: { [key: number]: IChessPieceData } = {}

function register(data: Partial<IChessPieceData>) {
  entitiesUtils.genericRegisterDataNum(
    namespace,
    data,
    _default,
    dataList,
    dataMap,
  )
}

export function get(type: EChessPieceType) {
  return entitiesUtils.genericGetDataNum(namespace, dataMap, type)
}

export function registerAll() {
  register({
    type: EChessPieceType.Null,
    name: 'Null',
    canMove: () => {
      return false
    },
    pointCost: 0,
  })

  register({
    type: EChessPieceType.Pawn,
    name: 'Pawn',
    frames: [sprites.chessPawnWhite[0], sprites.chessPawnBlack[0]],
    pointCost: 1,
    oy: 4,
    canMove: (obj: IChessPiece, tp: IChessTilemapPart) => {
      let tpi = tp.i
      let tpj = tp.j
      let i2 = obj.i
      let j2 = obj.j

      if (isRotated) {
        tpi = tp.j
        tpj = tp.i
        i2 = obj.j
        j2 = obj.i
      }

      let dx2 = tpi - i2
      let dy = Math.abs(tpj - j2)
      let dx = Math.abs(tpi - i2)

      let dxra = Math.abs(dx2)

      let dxd = dx2
      if (isRotated) {
        dxd = -dxd
      }
      if (obj.team === EChessTeam.White) {
        if (dxd < 0) {
          return false
        }
      }
      if (obj.team === EChessTeam.Black) {
        if (dxd > 0) {
          return false
        }
      }

      if (tp.pieces.length > 0) {
        return dy === 1 && dx === 1
      } else {
        if (dy !== 0) {
          return false
        }
        if (dx === 1) {
          return true
        }

        let isDoubleMoveAvailable = false
        if (isRotated) {
          isDoubleMoveAvailable =
            (obj.team === EChessTeam.Black && i2 === 1) ||
            (obj.team === EChessTeam.White && i2 === 8 - 2)
        } else {
          isDoubleMoveAvailable =
            (obj.team === EChessTeam.White && i2 === 1) ||
            (obj.team === EChessTeam.Black && i2 === 8 - 2)
        }

        if (isDoubleMoveAvailable) {
          if (dxra === 2) {
            if (isRotated) {
              let tp = chessTilemap.getSafe(obj.i, obj.j + dx2 / 2)
              if (tp && tp.pieces.length === 0) {
                return true
              }
            } else {
              let tp = chessTilemap.getSafe(obj.i + dx2 / 2, obj.j)
              if (tp && tp.pieces.length === 0) {
                return true
              }
            }
          }
        }

        return false
      }
    },
  })

  register({
    type: EChessPieceType.Rook,
    name: 'Rook',
    frames: [sprites.chessRookWhite[0], sprites.chessRookBlack[0]],
    pointCost: 5,
    canMove: (obj: IChessPiece, tp: IChessTilemapPart) => {
      let dy = Math.abs(tp.j - obj.j)
      let dx = Math.abs(tp.i - obj.i)

      if (dx !== 0 && dy !== 0) {
        return false
      }

      return chessPiecesUtil.checkLine(obj, tp)
    },
  })

  register({
    type: EChessPieceType.Bishop,
    name: 'Bishop',
    frames: [sprites.chessBishopWhite[0], sprites.chessBishopBlack[0]],
    pointCost: 3,
    oy: -4,
    canMove: (obj: IChessPiece, tp: IChessTilemapPart) => {
      let dy = Math.abs(tp.j - obj.j)
      let dx = Math.abs(tp.i - obj.i)
      if (dx !== dy) {
        return false
      }
      return chessPiecesUtil.checkLine(obj, tp)
    },
  })

  register({
    type: EChessPieceType.Knight,
    name: 'Knight',
    frames: [sprites.chessKnightWhite[0], sprites.chessKnightBlack[0]],
    pointCost: 3,
    oy: 3,
    canMove: (obj: IChessPiece, tp: IChessTilemapPart) => {
      let dy = Math.abs(tp.j - obj.j)
      let dx = Math.abs(tp.i - obj.i)
      return (dx === 2 && dy == 1) || (dx === 1 && dy == 2)
    },
  })

  register({
    type: EChessPieceType.Queen,
    name: 'Queen',
    frames: [sprites.chessQueenWhite[0], sprites.chessQueenBlack[0]],
    pointCost: 9,
    oy: -2,
    ox: -4,
    canMove: (obj: IChessPiece, tp: IChessTilemapPart) => {
      let dy = Math.abs(tp.j - obj.j)
      let dx = Math.abs(tp.i - obj.i)

      if (dx !== dy && dx !== 0 && dy !== 0) {
        return false
      }

      return chessPiecesUtil.checkLine(obj, tp)
    },
  })

  register({
    type: EChessPieceType.King,
    name: 'King',
    frames: [sprites.chessKingWhite[0], sprites.chessKingBlack[0]],
    pointCost: 1000,
    oy: -10,
    canMove: (obj: IChessPiece, tp: IChessTilemapPart) => {
      let dy = Math.abs(tp.j - obj.j)
      let dx = Math.abs(tp.i - obj.i)

      if (dx > 1 || dy > 1) {
        return false
      }
      return true
      // return dx + dy === 1
    },
  })
}
